import ManageAmenitiesServices from './Role-based/ManageAmenitiesServices';
import { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const AmenitySubCategory = () => {
  // State management
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentService, setCurrentService] = useState(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    duration: 1,
    isavailable: true,
    categoryid: '',
    has_items: true,
  });

  // Fetch services from API
  const fetchServices = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/`);
      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }
      const data = await response.json();
      setServices(data);
      setFilteredServices(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setError(error.message);
      setLoading(false);
      toast.error(`Error fetching services: ${error.message}`);
    }
  };

  // Fetch categories from API (if you have a separate endpoint for categories)
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/amenitycategories/`); // Adjust endpoint if needed
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error(`Error fetching categories: ${error.message}`);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const fetchData = async () => {
      await fetchCategories();
      await fetchServices();
    };
    fetchData();
  }, []);

  // Filter services based on search input
  useEffect(() => {
    if (search.trim() === '') {
      setFilteredServices(services);
    } else {
      const filtered = services.filter((service) =>
        service.name.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredServices(filtered);
    }
  }, [search, services]);

  // Group services by category
  const getServicesByCategory = (categoryId) => {
    return filteredServices.filter((service) => service.categoryid === categoryId);
  };

  // Check if category has any visible services after filtering
  const hasVisibleServices = (categoryId) => {
    if (search.trim() === '') return true;

    return filteredServices.some((service) => service.categoryid === categoryId);
  };

  // Modal handlers
  const handleCreateModalOpen = () => {
    setFormData({
      name: '',
      duration: 1,
      isavailable: true,
      categoryid: categories.length > 0 ? categories[0].id : '',
      has_items: true,
    });
    setIsCreateModalOpen(true);
  };

  const handleEditModalOpen = (service) => {
    setCurrentService(service);
    setFormData({
      name: service.name,
      duration: service.duration,
      isavailable: service.isavailable,
      categoryid: service.categoryid,
      has_items: service.has_items,
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteModalOpen = (service) => {
    setCurrentService(service);
    setIsDeleteModalOpen(true);
  };

  const handleModalClose = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setCurrentService(null);
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // API call handlers
  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          categoryid: Number(formData.categoryid),
          name: formData.name,
          duration: Number(formData.duration),
          isavailable: formData.isavailable,
          has_items: formData.has_items,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create service');
      }

      const newService = await response.json();
      setServices((prev) => [...prev, newService]);
      toast.success('Amenity service created successfully!');
      handleModalClose();
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error(`Error creating service: ${error.message}`);
    }
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/${currentService.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify({
          categoryid: Number(formData.categoryid),
          name: formData.name,
          duration: Number(formData.duration),
          isavailable: formData.isavailable,
          has_items: formData.has_items,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update service');
      }

      const updatedService = await response.json();
      setServices((prev) =>
        prev.map((service) => (service.id === currentService.id ? updatedService : service))
      );
      toast.success('Amenity service updated successfully!');
      handleModalClose();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error(`Error updating service: ${error.message}`);
    }
  };

  const handleDeleteService = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/${currentService.id}`, {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete service');
      }

      const result = await response.json();
      if (result.message) {
        toast.success(result.message);
      } else {
        toast.success('Amenity service deleted successfully!');
      }

      setServices((prev) => prev.filter((service) => service.id !== currentService.id));
      handleModalClose();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error(`Error deleting service: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-8xl mx-auto p-6">
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
          role="alert"
        >
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-8xl mx-auto p-6">
      <ToastContainer position="top-right" autoClose={5000} />
      <ManageAmenitiesServices />
      <div className="flex justify-between items-center mt-12 mb-4">
        <h1 className="text-2xl font-bold">Amenity Sub-Categories</h1>
      </div>

      <div className="relative mb-4 flex justify-between items-center">
        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search sub-categories..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border p-2 w-1/3 rounded-md pl-10"
        />
        <button
          className="bg-black text-white px-4 py-2 rounded-md shadow hover:text-white transition duration-200 flex items-center gap-2"
          onClick={handleCreateModalOpen}
        >
          <FaPlus /> Create New Sub-Category
        </button>
      </div>

      {categories
        .filter((category) => hasVisibleServices(category.id))
        .map((category) => {
          const categoryServices = getServicesByCategory(category.id);

          return (
            <div key={category.id} className="bg-white shadow-md rounded-md mb-6">
              <div className="p-2 border-b">
                <h2 className="text-lg font-bold">{category.name}</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-left text-sm font-semibold text-gray-600 border-b">
                      <th className="py-3 px-4 w-1/12">ID</th>
                      <th className="py-3 px-4 w-3/12">Sub-Category Name</th>
                      <th className="py-3 px-4 w-2/12">Duration (hrs)</th>
                      <th className="py-3 px-4 w-2/12">Available</th>
                      <th className="py-3 px-4 w-2/12">Has Items</th>
                      <th className="py-3 px-4 w-2/12 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryServices.length > 0 ? (
                      categoryServices.map((service) => (
                        <tr key={service.id} className="border-b text-gray-700 hover:bg-gray-50">
                          <td className="py-3 px-4">{service.id}</td>
                          <td className="py-3 px-4 font-medium">{service.name}</td>
                          <td className="py-3 px-4">{service.duration}</td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                service.isavailable
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {service.isavailable ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                service.has_items
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {service.has_items ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="py-3 px-4 flex justify-center gap-4">
                            <button
                              onClick={() => handleEditModalOpen(service)}
                              className="text-blue-500 hover:text-blue-700 transition duration-200"
                              title="Edit"
                            >
                              <FaEdit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteModalOpen(service)}
                              className="text-red-500 hover:text-red-700 transition duration-200"
                              title="Delete"
                            >
                              <FaTrash size={18} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="py-4 px-4 text-gray-500 text-center" colSpan="6">
                          No sub-categories found for this category
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

      {/* Create Service Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Create Amenity Sub-Category</h2>
              <form onSubmit={handleCreateService}>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                    Sub-Category Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="Enter sub-category name"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="duration">
                    Duration (hours)
                  </label>
                  <input
                    type="number"
                    id="duration"
                    name="duration"
                    min="1"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="Enter duration in hours"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="categoryid"
                  >
                    Category
                  </label>
                  <select
                    id="categoryid"
                    name="categoryid"
                    value={formData.categoryid}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  {/* Available to Customers Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label
                        htmlFor="isavailable"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Available to Customers
                      </label>
                      <p className="text-xs text-gray-500">
                        Toggle this ON to make this subcategory visible to customers.
                      </p>
                    </div>
                    <label
                      htmlFor="isavailable"
                      className="relative inline-flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id="isavailable"
                        name="isavailable"
                        checked={formData.isavailable}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.isavailable ? 'bg-black' : 'bg-gray-300'
                        }`}
                      ></div>
                      <div
                        className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                          formData.isavailable ? 'translate-x-6' : ''
                        }`}
                      ></div>
                    </label>
                  </div>

                  {/* Contains Items Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label
                        htmlFor="has_items"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Contains Items
                      </label>
                      <p className="text-xs text-gray-500">
                        Enable this if the subcategory includes services or items.
                      </p>
                    </div>
                    <label
                      htmlFor="has_items"
                      className="relative inline-flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id="has_items"
                        name="has_items"
                        checked={formData.has_items}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.has_items ? 'bg-black' : 'bg-gray-300'
                        }`}
                      ></div>
                      <div
                        className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                          formData.has_items ? 'translate-x-6' : ''
                        }`}
                      ></div>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="px-6 py-2 bg-red-500 text-white rounded hover:text-black transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-black text-white rounded hover:text-white transition duration-200"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {isEditModalOpen && currentService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Edit Amenity Sub-Category</h2>
              <form onSubmit={handleUpdateService}>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                    Sub-Category Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="Enter sub-category name"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="duration">
                    Duration (hours)
                  </label>
                  <input
                    type="number"
                    id="duration"
                    name="duration"
                    min="1"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="Enter duration in hours"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="block text-gray-700 text-sm font-bold mb-2"
                    htmlFor="categoryid"
                  >
                    Category
                  </label>
                  <select
                    id="categoryid"
                    name="categoryid"
                    value={formData.categoryid}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  {/* Available to Customers Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label
                        htmlFor="isavailable"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Available to Customers
                      </label>
                      <p className="text-xs text-gray-500">
                        Toggle this ON to make this subcategory visible to customers.
                      </p>
                    </div>
                    <label
                      htmlFor="isavailable"
                      className="relative inline-flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id="isavailable"
                        name="isavailable"
                        checked={formData.isavailable}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.isavailable ? 'bg-black' : 'bg-gray-300'
                        }`}
                      ></div>
                      <div
                        className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                          formData.isavailable ? 'translate-x-6' : ''
                        }`}
                      ></div>
                    </label>
                  </div>

                  {/* Contains Items Toggle */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label
                        htmlFor="has_items"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Contains Items
                      </label>
                      <p className="text-xs text-gray-500">
                        Enable this if the subcategory includes services or items.
                      </p>
                    </div>
                    <label
                      htmlFor="has_items"
                      className="relative inline-flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id="has_items"
                        name="has_items"
                        checked={formData.has_items}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <div
                        className={`w-12 h-6 rounded-full transition-colors ${
                          formData.has_items ? 'bg-black' : 'bg-gray-300'
                        }`}
                      ></div>
                      <div
                        className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                          formData.has_items ? 'translate-x-6' : ''
                        }`}
                      ></div>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="px-6 py-2 bg-red-500 text-white rounded hover:text-black transition duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-black text-white rounded hover:text-white transition duration-200"
                  >
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && currentService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
              <p className="mb-6">
                Are you sure you want to delete the amenity sub-category "{currentService.name}"?
                This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleModalClose}
                  className="px-4 py-2 bg-black text-white rounded hover:text-white transition duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteService}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:text-black transition duration-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmenitySubCategory;

// import ManageAmenitiesServices from "./Role-based/ManageAmenitiesServices";
// import { useState, useEffect } from "react";
// import { FaEdit, FaTrash, FaPlus, FaSearch } from "react-icons/fa";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const AmenitySubCategory = () => {
//   // State management
//   const [services, setServices] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [filteredServices, setFilteredServices] = useState([]);
//   const [search, setSearch] = useState("");
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   // Modal states
//   const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
//   const [isEditModalOpen, setIsEditModalOpen] = useState(false);
//   const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
//   const [currentService, setCurrentService] = useState(null);

//   // Form state for create/edit
//   const [formData, setFormData] = useState({
//     name: "",
//     duration: 1,
//     isavailable: true,
//     categoryid: "",
//     has_items: true,
//   });

//   // Fetch services from API
//   const fetchServices = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/`);
//       if (!response.ok) {
//         throw new Error("Failed to fetch services");
//       }
//       const data = await response.json();
//       setServices(data);
//       setFilteredServices(data);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching services:", error);
//       setError(error.message);
//       setLoading(false);
//       toast.error(`Error fetching services: ${error.message}`);
//     }
//   };

//   // Fetch categories from API (if you have a separate endpoint for categories)
//   const fetchCategories = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/amenitycategories/`); // Adjust endpoint if needed
//       if (!response.ok) {
//         throw new Error("Failed to fetch categories");
//       }
//       const data = await response.json();
//       setCategories(data);
//     } catch (error) {
//       console.error("Error fetching categories:", error);
//       toast.error(`Error fetching categories: ${error.message}`);
//     }
//   };

//   // Initial data fetch
//   useEffect(() => {
//     const fetchData = async () => {
//       await fetchCategories();
//       await fetchServices();
//     };
//     fetchData();
//   }, []);

//   // Filter services based on search input
//   useEffect(() => {
//     if (search.trim() === "") {
//       setFilteredServices(services);
//     } else {
//       const filtered = services.filter((service) =>
//         service.name.toLowerCase().includes(search.toLowerCase())
//       );
//       setFilteredServices(filtered);
//     }
//   }, [search, services]);

//   // Group services by category
//   const getServicesByCategory = (categoryId) => {
//     return filteredServices.filter(
//       (service) => service.categoryid === categoryId
//     );
//   };

//   // Modal handlers
//   const handleCreateModalOpen = () => {
//     setFormData({
//       name: "",
//       duration: 1,
//       isavailable: true,
//       categoryid: categories.length > 0 ? categories[0].id : "",
//       has_items: true,
//     });
//     setIsCreateModalOpen(true);
//   };

//   const handleEditModalOpen = (service) => {
//     setCurrentService(service);
//     setFormData({
//       name: service.name,
//       duration: service.duration,
//       isavailable: service.isavailable,
//       categoryid: service.categoryid,
//       has_items: service.has_items,
//     });
//     setIsEditModalOpen(true);
//   };

//   const handleDeleteModalOpen = (service) => {
//     setCurrentService(service);
//     setIsDeleteModalOpen(true);
//   };

//   const handleModalClose = () => {
//     setIsCreateModalOpen(false);
//     setIsEditModalOpen(false);
//     setIsDeleteModalOpen(false);
//     setCurrentService(null);
//   };

//   // Form handlers
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value,
//     }));
//   };

//   // API call handlers
//   const handleCreateService = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/amenityservices/`, {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//           accept: "application/json",
//         },
//         body: JSON.stringify({
//           categoryid: Number(formData.categoryid),
//           name: formData.name,
//           duration: Number(formData.duration),
//           isavailable: formData.isavailable,
//           has_items: formData.has_items,
//         }),
//       });

//       if (!response.ok) {
//         throw new Error("Failed to create service");
//       }

//       const newService = await response.json();
//       setServices((prev) => [...prev, newService]);
//       toast.success("Amenity service created successfully!");
//       handleModalClose();
//     } catch (error) {
//       console.error("Error creating service:", error);
//       toast.error(`Error creating service: ${error.message}`);
//     }
//   };

//   const handleUpdateService = async (e) => {
//     e.preventDefault();
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/amenityservices/${currentService.id}/`,
//         {
//           method: "PUT",
//           headers: {
//             "Content-Type": "application/json",
//             accept: "application/json",
//           },
//           body: JSON.stringify({
//             categoryid: Number(formData.categoryid),
//             name: formData.name,
//             duration: Number(formData.duration),
//             isavailable: formData.isavailable,
//             has_items: formData.has_items,
//           }),
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Failed to update service");
//       }

//       const updatedService = await response.json();
//       setServices((prev) =>
//         prev.map((service) =>
//           service.id === currentService.id ? updatedService : service
//         )
//       );
//       toast.success("Amenity service updated successfully!");
//       handleModalClose();
//     } catch (error) {
//       console.error("Error updating service:", error);
//       toast.error(`Error updating service: ${error.message}`);
//     }
//   };

//   const handleDeleteService = async () => {
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/amenityservices/${currentService.id}/`,
//         {
//           method: "DELETE",
//           headers: {
//             accept: "application/json",
//           },
//         }
//       );

//       if (!response.ok) {
//         throw new Error("Failed to delete service");
//       }

//       const result = await response.json();
//       if (result.message) {
//         toast.success(result.message);
//       } else {
//         toast.success("Amenity service deleted successfully!");
//       }

//       setServices((prev) =>
//         prev.filter((service) => service.id !== currentService.id)
//       );
//       handleModalClose();
//     } catch (error) {
//       console.error("Error deleting service:", error);
//       toast.error(`Error deleting service: ${error.message}`);
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="max-w-8xl mx-auto p-6">
//         <div
//           className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
//           role="alert"
//         >
//           <strong className="font-bold">Error!</strong>
//           <span className="block sm:inline"> {error}</span>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="max-w-8xl mx-auto p-6">
//       <ToastContainer position="top-right" autoClose={5000} />
//       <ManageAmenitiesServices />
//       <div className="flex justify-between items-center mt-12 mb-4">
//         <h1 className="text-2xl font-bold">Amenity Sub-Categories</h1>
//       </div>

//       <div className="relative mb-4 flex justify-between items-center">
//         <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//         <input
//           type="text"
//           placeholder="Search sub-categories..."
//           value={search}
//           onChange={(e) => setSearch(e.target.value)}
//           className="border p-2 w-1/3 rounded-md pl-10"
//         />
//         <button
//           className="bg-cyan-500 text-white px-4 py-2 rounded-md shadow hover:text-black transition duration-200 flex items-center gap-2"
//           onClick={handleCreateModalOpen}
//         >
//           <FaPlus /> Create New Sub-Category
//         </button>
//       </div>

//       {categories.map((category) => {
//         const categoryServices = getServicesByCategory(category.id);

//         return (
//           <div key={category.id} className="bg-white shadow-md rounded-md mb-6">
//             <div className="p-2 border-b">
//               <h2 className="text-lg font-bold">{category.name}</h2>
//             </div>

//             <div className="overflow-x-auto">
//               <table className="w-full border-collapse">
//                 <thead>
//                   <tr className="bg-gray-100 text-left text-sm font-semibold text-gray-600 border-b">
//                     <th className="py-3 px-4 w-1/12">ID</th>
//                     <th className="py-3 px-4 w-3/12">Sub-Category Name</th>
//                     <th className="py-3 px-4 w-2/12">Duration (hrs)</th>
//                     <th className="py-3 px-4 w-2/12">Available</th>
//                     <th className="py-3 px-4 w-2/12">Has Items</th>
//                     <th className="py-3 px-4 w-2/12 text-center">Action</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {categoryServices.length > 0 ? (
//                     categoryServices.map((service) => (
//                       <tr
//                         key={service.id}
//                         className="border-b text-gray-700 hover:bg-gray-50"
//                       >
//                         <td className="py-3 px-4">{service.id}</td>
//                         <td className="py-3 px-4 font-medium">
//                           {service.name}
//                         </td>
//                         <td className="py-3 px-4">{service.duration}</td>
//                         <td className="py-3 px-4">
//                           <span
//                             className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
//                               service.isavailable
//                                 ? "bg-green-100 text-green-800"
//                                 : "bg-red-100 text-red-800"
//                             }`}
//                           >
//                             {service.isavailable ? "Yes" : "No"}
//                           </span>
//                         </td>
//                         <td className="py-3 px-4">
//                           <span
//                             className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
//                               service.has_items
//                                 ? "bg-green-100 text-green-800"
//                                 : "bg-red-100 text-red-800"
//                             }`}
//                           >
//                             {service.has_items ? "Yes" : "No"}
//                           </span>
//                         </td>
//                         <td className="py-3 px-4 flex justify-center gap-4">
//                           <button
//                             onClick={() => handleEditModalOpen(service)}
//                             className="text-blue-500 hover:text-blue-700 transition duration-200"
//                             title="Edit"
//                           >
//                             <FaEdit size={18} />
//                           </button>
//                           <button
//                             onClick={() => handleDeleteModalOpen(service)}
//                             className="text-red-500 hover:text-red-700 transition duration-200"
//                             title="Delete"
//                           >
//                             <FaTrash size={18} />
//                           </button>
//                         </td>
//                       </tr>
//                     ))
//                   ) : (
//                     <tr>
//                       <td
//                         className="py-4 px-4 text-gray-500 text-center"
//                         colSpan="6"
//                       >
//                         No sub-categories found for this category
//                       </td>
//                     </tr>
//                   )}
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         );
//       })}

//       {/* Create Service Modal */}
//       {isCreateModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
//             <div className="p-6">
//               <h2 className="text-xl font-semibold mb-4">
//                 Create Amenity Sub-Category
//               </h2>
//               <form onSubmit={handleCreateService}>
//                 <div className="mb-4">
//                   <label
//                     className="block text-gray-700 text-sm font-bold mb-2"
//                     htmlFor="name"
//                   >
//                     Sub-Category Name
//                   </label>
//                   <input
//                     type="text"
//                     id="name"
//                     name="name"
//                     value={formData.name}
//                     onChange={handleInputChange}
//                     className="w-full p-2 border rounded"
//                     placeholder="Enter sub-category name"
//                     required
//                   />
//                 </div>
//                 <div className="mb-4">
//                   <label
//                     className="block text-gray-700 text-sm font-bold mb-2"
//                     htmlFor="duration"
//                   >
//                     Duration (hours)
//                   </label>
//                   <input
//                     type="number"
//                     id="duration"
//                     name="duration"
//                     min="1"
//                     value={formData.duration}
//                     onChange={handleInputChange}
//                     className="w-full p-2 border rounded"
//                     placeholder="Enter duration in hours"
//                     required
//                   />
//                 </div>
//                 <div className="mb-4">
//                   <label
//                     className="block text-gray-700 text-sm font-bold mb-2"
//                     htmlFor="categoryid"
//                   >
//                     Category
//                   </label>
//                   <select
//                     id="categoryid"
//                     name="categoryid"
//                     value={formData.categoryid}
//                     onChange={handleInputChange}
//                     className="w-full p-2 border rounded"
//                     required
//                   >
//                     {categories.map((category) => (
//                       <option key={category.id} value={category.id}>
//                         {category.name}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//                 <div className="grid grid-cols-1 gap-4 mb-4">
//                   {/* Available to Customers Toggle */}
//                   <div className="flex items-center justify-between">
//                     <div>
//                       <label
//                         htmlFor="isavailable"
//                         className="block text-sm font-medium text-gray-700 mb-1"
//                       >
//                         Available to Customers
//                       </label>
//                       <p className="text-xs text-gray-500">
//                         Toggle this ON to make this subcategory visible to
//                         customers.
//                       </p>
//                     </div>
//                     <label
//                       htmlFor="isavailable"
//                       className="relative inline-flex items-center cursor-pointer"
//                     >
//                       <input
//                         type="checkbox"
//                         id="isavailable"
//                         name="isavailable"
//                         checked={formData.isavailable}
//                         onChange={handleInputChange}
//                         className="sr-only"
//                       />
//                       <div
//                         className={`w-12 h-6 rounded-full transition-colors ${
//                           formData.isavailable ? "bg-black" : "bg-gray-300"
//                         }`}
//                       ></div>
//                       <div
//                         className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
//                           formData.isavailable ? "translate-x-6" : ""
//                         }`}
//                       ></div>
//                     </label>
//                   </div>

//                   {/* Contains Items Toggle */}
//                   <div className="flex items-center justify-between">
//                     <div>
//                       <label
//                         htmlFor="has_items"
//                         className="block text-sm font-medium text-gray-700 mb-1"
//                       >
//                         Contains Items
//                       </label>
//                       <p className="text-xs text-gray-500">
//                         Enable this if the subcategory includes services or
//                         items.
//                       </p>
//                     </div>
//                     <label
//                       htmlFor="has_items"
//                       className="relative inline-flex items-center cursor-pointer"
//                     >
//                       <input
//                         type="checkbox"
//                         id="has_items"
//                         name="has_items"
//                         checked={formData.has_items}
//                         onChange={handleInputChange}
//                         className="sr-only"
//                       />
//                       <div
//                         className={`w-12 h-6 rounded-full transition-colors ${
//                           formData.has_items ? "bg-black" : "bg-gray-300"
//                         }`}
//                       ></div>
//                       <div
//                         className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
//                           formData.has_items ? "translate-x-6" : ""
//                         }`}
//                       ></div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="flex justify-end gap-3">
//                   <button
//                     type="button"
//                     onClick={handleModalClose}
//                     className="px-6 py-2 bg-red-500 text-white rounded hover:text-black transition duration-200"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-6 py-2 bg-cyan-500 text-white rounded hover:text-black transition duration-200"
//                   >
//                     Create
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Edit Service Modal */}
//       {isEditModalOpen && currentService && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
//             <div className="p-6">
//               <h2 className="text-xl font-semibold mb-4">
//                 Edit Amenity Sub-Category
//               </h2>
//               <form onSubmit={handleUpdateService}>
//                 <div className="mb-4">
//                   <label
//                     className="block text-gray-700 text-sm font-bold mb-2"
//                     htmlFor="name"
//                   >
//                     Sub-Category Name
//                   </label>
//                   <input
//                     type="text"
//                     id="name"
//                     name="name"
//                     value={formData.name}
//                     onChange={handleInputChange}
//                     className="w-full p-2 border rounded"
//                     placeholder="Enter sub-category name"
//                     required
//                   />
//                 </div>
//                 <div className="mb-4">
//                   <label
//                     className="block text-gray-700 text-sm font-bold mb-2"
//                     htmlFor="duration"
//                   >
//                     Duration (hours)
//                   </label>
//                   <input
//                     type="number"
//                     id="duration"
//                     name="duration"
//                     min="1"
//                     value={formData.duration}
//                     onChange={handleInputChange}
//                     className="w-full p-2 border rounded"
//                     placeholder="Enter duration in hours"
//                     required
//                   />
//                 </div>
//                 <div className="mb-4">
//                   <label
//                     className="block text-gray-700 text-sm font-bold mb-2"
//                     htmlFor="categoryid"
//                   >
//                     Category
//                   </label>
//                   <select
//                     id="categoryid"
//                     name="categoryid"
//                     value={formData.categoryid}
//                     onChange={handleInputChange}
//                     className="w-full p-2 border rounded"
//                     required
//                   >
//                     {categories.map((category) => (
//                       <option key={category.id} value={category.id}>
//                         {category.name}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//                 <div className="grid grid-cols-1 gap-4 mb-4">
//                   {/* Available to Customers Toggle */}
//                   <div className="flex items-center justify-between">
//                     <div>
//                       <label
//                         htmlFor="isavailable"
//                         className="block text-sm font-medium text-gray-700 mb-1"
//                       >
//                         Available to Customers
//                       </label>
//                       <p className="text-xs text-gray-500">
//                         Toggle this ON to make this subcategory visible to
//                         customers.
//                       </p>
//                     </div>
//                     <label
//                       htmlFor="isavailable"
//                       className="relative inline-flex items-center cursor-pointer"
//                     >
//                       <input
//                         type="checkbox"
//                         id="isavailable"
//                         name="isavailable"
//                         checked={formData.isavailable}
//                         onChange={handleInputChange}
//                         className="sr-only"
//                       />
//                       <div
//                         className={`w-12 h-6 rounded-full transition-colors ${
//                           formData.isavailable ? "bg-black" : "bg-gray-300"
//                         }`}
//                       ></div>
//                       <div
//                         className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
//                           formData.isavailable ? "translate-x-6" : ""
//                         }`}
//                       ></div>
//                     </label>
//                   </div>

//                   {/* Contains Items Toggle */}
//                   <div className="flex items-center justify-between">
//                     <div>
//                       <label
//                         htmlFor="has_items"
//                         className="block text-sm font-medium text-gray-700 mb-1"
//                       >
//                         Contains Items
//                       </label>
//                       <p className="text-xs text-gray-500">
//                         Enable this if the subcategory includes services or
//                         items.
//                       </p>
//                     </div>
//                     <label
//                       htmlFor="has_items"
//                       className="relative inline-flex items-center cursor-pointer"
//                     >
//                       <input
//                         type="checkbox"
//                         id="has_items"
//                         name="has_items"
//                         checked={formData.has_items}
//                         onChange={handleInputChange}
//                         className="sr-only"
//                       />
//                       <div
//                         className={`w-12 h-6 rounded-full transition-colors ${
//                           formData.has_items ? "bg-black" : "bg-gray-300"
//                         }`}
//                       ></div>
//                       <div
//                         className={`dot absolute left-1 top-1 w-4 h-4 rounded-full bg-white transition-transform ${
//                           formData.has_items ? "translate-x-6" : ""
//                         }`}
//                       ></div>
//                     </label>
//                   </div>
//                 </div>

//                 <div className="flex justify-end gap-3">
//                   <button
//                     type="button"
//                     onClick={handleModalClose}
//                     className="px-6 py-2 bg-red-500 text-white rounded hover:text-black transition duration-200"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="submit"
//                     className="px-6 py-2 bg-cyan-500 text-white rounded hover:text-black transition duration-200"
//                   >
//                     Update
//                   </button>
//                 </div>
//               </form>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Delete Confirmation Modal */}
//       {isDeleteModalOpen && currentService && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
//             <div className="p-6">
//               <h2 className="text-xl font-semibold mb-4">Confirm Deletion</h2>
//               <p className="mb-6">
//                 Are you sure you want to delete the amenity sub-category "
//                 {currentService.name}"? This action cannot be undone.
//               </p>
//               <div className="flex justify-end gap-3">
//                 <button
//                   onClick={handleModalClose}
//                   className="px-4 py-2 bg-cyan-500 text-white rounded hover:text-black transition duration-200"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={handleDeleteService}
//                   className="px-4 py-2 bg-red-500 text-white rounded hover:text-black transition duration-200"
//                 >
//                   Delete
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default AmenitySubCategory;
